import { useEffect, useState, lazy, Suspense } from "react";
import landingPagePreview from "@/assets/chat-previews/landing-page.webp";
import adCreativesPreview from "@/assets/chat-previews/ad-creatives.webp";
import reportCoverPreview from "@/assets/chat-previews/report-cover.webp";
import seoDashboardPreview from "@/assets/chat-previews/seo-dashboard.webp";
import contentCarouselPreview from "@/assets/chat-previews/content-carousel.webp";
import codeBuildPreview from "@/assets/chat-previews/code-build.webp";
import internalAppPreview from "@/assets/chat-previews/internal-app.webp";
import launchPagePreview from "@/assets/chat-previews/launch-page.webp";
import boardDeckPreview from "@/assets/chat-previews/board-deck.webp";
import wbrDashboardPreview from "@/assets/chat-previews/wbr-dashboard.webp";
import ugcInfluencerPreview from "@/assets/chat-previews/ugc-influencer.webp";

/**
 * Mane-style floating hero chat.
 *
 * Three pieces of glass floating over the video:
 *   1. a faint user line at the top
 *   2. a richer agent reply with a "Mane Agent" label (optional image preview)
 *   3. a deliverable card with the result + real brand logos of tools used
 *
 * Copy is intentionally plain. Same line should land for a solo founder, a
 * head of marketing at a 500-person company, and a dev shipping a feature.
 */

type Deliverable = {
  title: string;
  label: string;
  value: string;
  positive?: boolean;
};

// `slug` is the simple-icons slug (https://simpleicons.org). We pull a
// pre-colored SVG from the CDN so the logos look like the real brand.
type Integration = { name: string; slug: string; light?: boolean };

type Surface = "mane" | "slack" | "imessage" | "telegram" | "whatsapp" | "teams";

type Attachment = { src: string; caption: string; alt: string };

type Script = {
  dept: string;
  user: string;
  agent: string;
  deliverable: Deliverable;
  integrations: Integration[];
  surface?: Surface;
  channel?: string; // for slack
  sender?: string;  // user name for slack/imessage
  attachment?: Attachment; // inline image preview rendered with agent reply
  proactive?: boolean; // Mane initiates without a prompt
};

// Centralised integration registry. `light: true` forces a white render
// for brands whose mark is black (GitHub, Vercel, Notion, Apple…) so they
// stay legible on the dark glass card.
const INT = {
  // Sales / CRM
  hubspot: { name: "HubSpot", slug: "hubspot" },
  salesforce: { name: "Salesforce", slug: "salesforce" },
  pipedrive: { name: "Pipedrive", slug: "pipedrive", light: true },
  attio: { name: "Attio", slug: "attio", light: true },
  // Messaging / email
  slack: { name: "Slack", slug: "slack" },
  gmail: { name: "Gmail", slug: "gmail" },
  outlook: { name: "Outlook", slug: "microsoftoutlook" },
  intercom: { name: "Intercom", slug: "intercom" },
  zendesk: { name: "Zendesk", slug: "zendesk" },
  // Marketing / ads / analytics
  meta: { name: "Meta Ads", slug: "meta" },
  googleads: { name: "Google Ads", slug: "googleads" },
  ga: { name: "GA4", slug: "googleanalytics" },
  linkedin: { name: "LinkedIn", slug: "linkedin" },
  // Design / content
  notion: { name: "Notion", slug: "notion", light: true },
  canva: { name: "Canva", slug: "canva" },
  tailwind: { name: "Tailwind", slug: "tailwindcss" },
  // Dev / build
  github: { name: "GitHub", slug: "github", light: true },
  linear: { name: "Linear", slug: "linear" },
  sentry: { name: "Sentry", slug: "sentry" },
  vercel: { name: "Vercel", slug: "vercel", light: true },
  cypress: { name: "Cypress", slug: "cypress", light: true },
  // Money / ops / HR
  stripe: { name: "Stripe", slug: "stripe" },
  quickbooks: { name: "QuickBooks", slug: "quickbooks" },
  brex: { name: "Brex", slug: "brex", light: true },
  rippling: { name: "Rippling", slug: "rippling", light: true },
  // Storage / docs
  drive: { name: "Drive", slug: "googledrive" },
  gcal: { name: "Calendar", slug: "googlecalendar" },
  // Hiring
  greenhouse: { name: "Greenhouse", slug: "greenhouse" },
  // Social / influencer
  instagram: { name: "Instagram", slug: "instagram" },
  tiktok: { name: "TikTok", slug: "tiktok", light: true },
  youtube: { name: "YouTube", slug: "youtube" },
  x: { name: "X", slug: "x", light: true },
  // Lead sourcing / enrichment
  apollo: { name: "Apollo", slug: "apollo" },
  clay: { name: "Clay", slug: "clay", light: true },
  // SEO / GEO / search
  gsc: { name: "Search Console", slug: "googlesearchconsole" },
  ahrefs: { name: "Ahrefs", slug: "ahrefs" },
  semrush: { name: "Semrush", slug: "semrush" },
  perplexity: { name: "Perplexity", slug: "perplexity", light: true },
  webflow: { name: "Webflow", slug: "webflow" },
  // Security / observability
  snyk: { name: "Snyk", slug: "snyk" },
  onepassword: { name: "1Password", slug: "1password" },
  cloudflare: { name: "Cloudflare", slug: "cloudflare" },
  datadog: { name: "Datadog", slug: "datadog" },
  // Product / data / PM
  jira: { name: "Jira", slug: "jira" },
  asana: { name: "Asana", slug: "asana" },
  posthog: { name: "PostHog", slug: "posthog" },
  amplitude: { name: "Amplitude", slug: "amplitude", light: true },
  segment: { name: "Segment", slug: "segment" },
  mixpanel: { name: "Mixpanel", slug: "mixpanel", light: true },
  // VA / scheduling / docs
  calendly: { name: "Calendly", slug: "calendly" },
  zoom: { name: "Zoom", slug: "zoom" },
  docusign: { name: "DocuSign", slug: "docusign" },
  // Commerce / lifecycle
  shopify: { name: "Shopify", slug: "shopify" },
  klaviyo: { name: "Klaviyo", slug: "klaviyo" },
  mailchimp: { name: "Mailchimp", slug: "mailchimp" },
} as const;

const SCRIPTS: Script[] = [
  // — Solopreneur / small team —
  {
    surface: "imessage",
    sender: "You",
    dept: "Inbox",
    user: "Clear my inbox. Reply to the easy ones.",
    agent:
      "Triaged 84 emails. 12 quick replies drafted, 6 sent automatically, 4 booked to your calendar.",
    deliverable: { title: "Inbox zero", label: "Replies drafted", value: "12", positive: true },
    integrations: [INT.gmail, INT.gcal, INT.notion],
  },
  {
    surface: "telegram",
    dept: "Invoicing",
    user: "Chase the clients who haven't paid me yet.",
    agent:
      "Found 7 overdue invoices in Stripe. Polite reminder sent to each, 2 already paid while we talked.",
    deliverable: { title: "Overdue collected", label: "Paid this hour", value: "$8,420", positive: true },
    integrations: [INT.stripe, INT.quickbooks, INT.gmail],
  },
  {
    surface: "slack",
    channel: "marketing",
    sender: "Maya",
    dept: "Content",
    user: "Write this week's newsletter from my latest blog posts.",
    agent:
      "Pulled your 3 newest posts, drafted a 6-paragraph email with subject line A/B options, ready to send.",
    deliverable: { title: "Newsletter drafted", label: "Open-rate forecast", value: "42%", positive: true },
    integrations: [INT.notion, INT.gmail, INT.linkedin],
  },

  // — Sales / Marketing / CS —
  {
    surface: "whatsapp",
    channel: "sales",
    sender: "David",
    dept: "Sales",
    user: "Which deals in pipeline are slipping?",
    agent:
      "12 of 184 open opps stalled over 14 days. Nudge emails drafted to each owner, briefs in Slack.",
    deliverable: { title: "Pipeline review", label: "At risk", value: "12 deals", positive: false },
    integrations: [INT.hubspot, INT.salesforce, INT.slack],
  },
  {
    surface: "mane",
    dept: "Marketing",
    user: "Our ad spend on Meta tanked. Fix it.",
    agent:
      "Creative #4 fatigued, CTR down 42%. Paused it, shifted spend to top two, drafted 3 new variants.",
    deliverable: { title: "Ads rebalanced", label: "Recovered ROAS", value: "+38%", positive: true },
    integrations: [INT.meta, INT.googleads, INT.ga, INT.canva],
  },
  {
    surface: "telegram",
    sender: "You",
    dept: "Customer Success",
    user: "Who's about to churn this week?",
    agent:
      "Cross-checked usage, NPS and tickets. 6 accounts flagged. Save plays drafted with talking points.",
    deliverable: { title: "At-risk accounts", label: "Flagged", value: "6", positive: false },
    integrations: [INT.intercom, INT.zendesk, INT.hubspot],
  },

  // — Builders / Devs —
  {
    surface: "slack",
    channel: "engineering",
    sender: "Jordan",
    dept: "Build",
    user: "Build the new onboarding flow. Three steps, auth, then dashboard.",
    agent:
      "Designed and coded the pages from your brand tokens, wired auth, wrote tests. Preview is live, 24/24 green. PR open for review.",
    deliverable: { title: "Feature shipped", label: "Preview", value: "✓ Live", positive: true },
    integrations: [INT.github, INT.vercel, INT.tailwind, INT.cypress],
  },
  {
    surface: "whatsapp",
    dept: "Engineering",
    user: "Webhook retries are failing in prod. Triage and fix.",
    agent:
      "Idempotency key collision on the retry path. Patch is on fix/webhook-retries with tests, PR open.",
    deliverable: { title: "Pull request", label: "PR #482", value: "Ready", positive: true },
    integrations: [INT.github, INT.sentry, INT.linear],
  },
  {
    surface: "imessage",
    sender: "You",
    dept: "Preview",
    user: "Spin up a staging env so the client can try it.",
    agent:
      "Branch deployed to Vercel preview, seeded with demo data. Link shared in Slack with login.",
    deliverable: { title: "Preview deployed", label: "Build time", value: "47s", positive: true },
    integrations: [INT.vercel, INT.github, INT.slack],
  },

  // — Operations / Finance / HR —
  {
    surface: "slack",
    channel: "exec",
    sender: "Priya",
    dept: "Operations",
    user: "Board pack for Tuesday. MRR, burn, pipeline, headcount.",
    agent:
      "Pulled Stripe, Brex, HubSpot, Rippling. 14 slides drafted with narrative in speaker notes.",
    attachment: {
      src: boardDeckPreview,
      caption: "q2-board-pack.key · 14 slides · narrative in notes",
      alt: "Board deck slide showing MRR, burn, and pipeline KPIs",
    },
    deliverable: { title: "Board deck", label: "Slides ready", value: "14", positive: true },
    integrations: [INT.stripe, INT.brex, INT.hubspot, INT.rippling],
  },
  {
    surface: "mane",
    dept: "Finance",
    user: "Reconcile April invoices against Stripe payouts.",
    agent:
      "Matched 312 invoices to 47 payouts. 3 anomalies flagged, owners pinged with the variance.",
    deliverable: { title: "Variance found", label: "Unaccounted", value: "$4,210", positive: false },
    integrations: [INT.stripe, INT.quickbooks, INT.notion],
  },
  {
    surface: "telegram",
    sender: "You",
    dept: "Recruiting",
    user: "Source 5 senior backend engineers in NYC.",
    agent:
      "Scanned LinkedIn and your ATS. 38 matches, 12 warm, 5 shortlisted with personalised intro notes.",
    deliverable: { title: "Shortlist ready", label: "Top candidates", value: "5", positive: true },
    integrations: [INT.linkedin, INT.greenhouse, INT.gmail],
  },

  // — Memory recall —
  {
    surface: "imessage",
    sender: "You",
    dept: "Memory",
    user: "What did we promise Acme on the call back in February?",
    agent:
      "Found it. Feb 14 call with Sarah Chen: SOC 2 report by Q2, SAML by June, dedicated CSM at renewal. All three are tracked in Linear and on the renewal brief.",
    deliverable: { title: "Recalled in 1.2s", label: "Commitments tracked", value: "3 / 3", positive: true },
    integrations: [INT.notion, INT.linear, INT.slack],
  },
  {
    surface: "slack",
    channel: "product",
    sender: "Nina",
    dept: "Memory",
    user: "Why did we kill the referral program last quarter?",
    agent:
      "Q3 retro, Oct 11: CAC payback blew past 14 months, fraud rate hit 9%, legal flagged the sweepstakes wording. Full thread + decision doc linked.",
    deliverable: { title: "Context restored", label: "Sources cited", value: "4", positive: true },
    integrations: [INT.notion, INT.slack, INT.drive],
  },

  // — Landing pages / web —
  {
    surface: "mane",
    dept: "Web",
    user: "Build a landing page for the new pricing tier. Match our brand.",
    agent:
      "Designed and coded it end-to-end. Hero, features, FAQ, pricing table, all in your brand voice. Deployed to Vercel, Lighthouse 98 / 100 / 100 / 100.",
    deliverable: { title: "Landing page live", label: "Lighthouse", value: "98", positive: true },
    integrations: [INT.tailwind, INT.vercel, INT.github],
  },
  {
    surface: "telegram",
    sender: "You",
    dept: "Web",
    user: "Spin up a launch page for tomorrow's product drop.",
    agent:
      "Wrote copy from the PRD, generated hero imagery, wired the waitlist to HubSpot, shipped to Vercel. Share link ready.",
    attachment: {
      src: launchPagePreview,
      caption: "drop.mane.dev · live · waitlist wired to HubSpot",
      alt: "Premium product drop landing page with serif headline and amber CTA",
    },
    deliverable: { title: "Launch page shipped", label: "Time to live", value: "6 min", positive: true },
    integrations: [INT.tailwind, INT.vercel, INT.hubspot],
  },

  // — Lead gen + email nurture —
  {
    surface: "mane",
    dept: "Demand Gen",
    user: "Find 200 Series B SaaS VPs of Marketing in the US. Start nurturing.",
    agent:
      "Sourced 217 from Apollo and Clay, enriched, deduped against HubSpot. 4-step nurture written in your voice, sending Tue 9am local.",
    deliverable: { title: "Sequence live", label: "Net-new leads", value: "217", positive: true },
    integrations: [INT.apollo, INT.clay, INT.hubspot, INT.gmail],
  },
  {
    surface: "slack",
    channel: "growth",
    sender: "Priya",
    dept: "Nurture",
    user: "Re-engage the cold leads from Q1. Personalise it.",
    agent:
      "1,842 cold contacts. Drafted 6 variants by persona, references their last touch and recent funding. Reply-handling on, calendar links live.",
    deliverable: { title: "Win-back launched", label: "Forecast meetings", value: "38", positive: true },
    integrations: [INT.hubspot, INT.gmail, INT.linkedin, INT.gcal],
  },

  // — Ads & creative —
  {
    surface: "mane",
    dept: "Paid Social",
    user: "Launch a Meta campaign for the spring drop. Make the creative too.",
    agent:
      "Generated 9 ad creatives in your brand kit, 3 hooks each, vertical + square. Campaign live with $80 / day, audiences cloned from top performer.",
    deliverable: { title: "Campaign live", label: "Creatives shipped", value: "9", positive: true },
    integrations: [INT.meta, INT.canva, INT.ga, INT.googleads],
  },
  {
    surface: "telegram",
    sender: "You",
    dept: "Creative",
    user: "Make 5 static ads for Black Friday. Vertical and square.",
    agent:
      "Five concepts, two sizes each. Copy A/B tested against last year's winners. Renders ready to push to Meta and Google.",
    deliverable: { title: "Ad set ready", label: "Assets generated", value: "10", positive: true },
    integrations: [INT.canva, INT.meta, INT.googleads, INT.tiktok],
  },

  // — Influencer / social —
  {
    surface: "whatsapp",
    dept: "Influencer",
    user: "Run an influencer campaign for the launch. Mid-tier creators only.",
    agent:
      "Shortlisted 24 creators across TikTok and Instagram, 50K to 250K, audience fit > 0.8. Outreach sent, 7 already replied with rates.",
    attachment: {
      src: ugcInfluencerPreview,
      caption: "24 creators · TikTok + Instagram · 7 replies in",
      alt: "Moodboard of three creator content stills for an influencer campaign launch",
    },
    deliverable: { title: "Outreach live", label: "Creators contacted", value: "24", positive: true },
    integrations: [INT.tiktok, INT.instagram, INT.youtube, INT.notion],
  },
  {
    surface: "slack",
    channel: "social",
    sender: "Maya",
    dept: "Social",
    user: "Plan next month's content across all channels.",
    agent:
      "31 posts mapped: 12 TikTok, 10 Instagram, 6 X, 3 LinkedIn. Hooks, captions and visuals drafted, queued in your scheduler.",
    deliverable: { title: "Calendar booked", label: "Posts queued", value: "31", positive: true },
    integrations: [INT.tiktok, INT.instagram, INT.x, INT.linkedin],
  },

  // — Landing pages (coded) —
  {
    surface: "imessage",
    sender: "You",
    dept: "Web",
    user: "Code a landing page for the webinar. Real Next.js, not a builder.",
    agent:
      "App router page shipped, Tailwind tokens from your design system, registration wired to HubSpot, OG image generated. Lighthouse 99.",
    deliverable: { title: "Page coded + deployed", label: "Lighthouse", value: "99", positive: true },
    integrations: [INT.github, INT.vercel, INT.tailwind, INT.hubspot],
  },

  // — Building stuff (with image attachments) —
  {
    surface: "imessage",
    sender: "You",
    dept: "Web",
    user: "Build me a real landing page for the new pricing tier.",
    agent:
      "Designed, coded, and deployed it. Brand tokens pulled from your site, copy from your PRD, shipped to Vercel. Live preview attached.",
    attachment: {
      src: landingPagePreview,
      caption: "preview · pricing.mane.dev · Lighthouse 99",
      alt: "Landing page preview with hero, pricing table, and CTAs",
    },
    deliverable: { title: "Landing page shipped", label: "Lighthouse", value: "99", positive: true },
    integrations: [INT.tailwind, INT.vercel, INT.github, INT.webflow],
  },
  {
    surface: "slack",
    channel: "engineering",
    sender: "Jordan",
    dept: "Build",
    user: "Ship the new onboarding flow. Three steps, Stripe at the end.",
    agent:
      "Built the pages, wired Stripe Checkout, wrote 18 tests, all green. Preview is live, PR open.",
    attachment: {
      src: codeBuildPreview,
      caption: "branch · feat/onboarding · 18 / 18 tests green",
      alt: "Code editor screenshot showing the onboarding flow being built",
    },
    deliverable: { title: "Feature shipped", label: "PR", value: "#512 ready", positive: true },
    integrations: [INT.github, INT.vercel, INT.stripe, INT.cypress],
  },

  // — Ads & creative (with image) —
  {
    surface: "telegram",
    sender: "You",
    dept: "Creative",
    user: "Make 3 spring drop ads. Premium feel, warm tones.",
    agent:
      "Three creatives generated in your brand kit, vertical 4:5 for Meta and TikTok. Hook copy A/B tested against last year's winners.",
    attachment: {
      src: adCreativesPreview,
      caption: "spring-drop-v3 · 3 creatives · ready for Meta + TikTok",
      alt: "Three spring fashion ad creatives in warm amber tones",
    },
    deliverable: { title: "Creatives ready", label: "Assets generated", value: "3", positive: true },
    integrations: [INT.canva, INT.meta, INT.tiktok, INT.instagram],
  },

  // — Reports —
  {
    surface: "mane",
    dept: "Research",
    user: "Build me the Q2 market intelligence brief for the board.",
    agent:
      "Pulled funding, hiring and product signals on the top 40 competitors. 22-page brief with charts and a one-page exec summary up front.",
    attachment: {
      src: reportCoverPreview,
      caption: "Q2-market-intel.pdf · 22 pages · cited",
      alt: "Premium PDF report cover, Q2 Market Intelligence Brief",
    },
    deliverable: { title: "Report ready", label: "Sources cited", value: "147", positive: true },
    integrations: [INT.perplexity, INT.notion, INT.drive],
  },

  // — SEO / GEO (AI search) —
  {
    surface: "slack",
    channel: "growth",
    sender: "Priya",
    dept: "SEO",
    user: "Why is our organic traffic flat this month? Fix it.",
    agent:
      "Found 14 pages that dropped on intent-heavy queries. Rewrote titles + H1s, added FAQ schema, internal links from your 5 hubs. Re-submitted to GSC.",
    attachment: {
      src: seoDashboardPreview,
      caption: "organic · last 90 days · +38% projected",
      alt: "SEO analytics dashboard with rising organic traffic line chart",
    },
    deliverable: { title: "SEO fixed", label: "Pages rewritten", value: "14", positive: true },
    integrations: [INT.gsc, INT.ahrefs, INT.semrush, INT.notion],
  },
  {
    surface: "mane",
    dept: "GEO",
    user: "Make sure ChatGPT and Perplexity recommend us, not our competitors.",
    agent:
      "Audited 32 AI-search queries in your category. We were cited 7/32, competitor 24/32. Published 6 authoritative comparison pages, pitched 4 source citations.",
    deliverable: { title: "AI visibility brief", label: "Cited mentions", value: "7 → 24", positive: true },
    integrations: [INT.perplexity, INT.gsc, INT.notion, INT.linkedin],
  },

  // — Content creation (with image) —
  {
    surface: "whatsapp",
    dept: "Content",
    user: "Plan and design next week's Instagram carousels.",
    agent:
      "5 carousels mapped to your content pillars. Visuals generated in brand style, captions and hashtags drafted, queued in your scheduler for 9am drops.",
    attachment: {
      src: contentCarouselPreview,
      caption: "ig-week-22 · 5 carousels · queued Mon–Fri 9am",
      alt: "Instagram carousel mockup with warm amber lifestyle imagery",
    },
    deliverable: { title: "Content scheduled", label: "Posts queued", value: "5", positive: true },
    integrations: [INT.instagram, INT.canva, INT.tiktok],
  },

  // — Self-improvement / skill creation —
  {
    surface: "mane",
    dept: "Self-improvement",
    user: "Nice work on that report. Remember how you did it.",
    agent:
      "Saved the workflow as a reusable skill: `q-market-intel`. Pulled the steps that worked, dropped the dead ends, indexed the sources. Next quarter's brief will run in 1 prompt.",
    deliverable: { title: "Skill created", label: "Steps compressed", value: "47 → 6", positive: true },
    integrations: [INT.notion, INT.drive, INT.perplexity],
  },
  {
    surface: "slack",
    channel: "ops",
    sender: "Priya",
    dept: "Learning",
    user: "Board deck was great. Do it like this every month.",
    agent:
      "Promoted last night's run to a recurring skill: `monthly-board-pack`. Learned your tone, slide order, and the three charts you always cut. Scheduled for the 1st at 7am.",
    deliverable: { title: "Skill promoted", label: "Recurring", value: "Monthly · 7am", positive: true },
    integrations: [INT.stripe, INT.brex, INT.hubspot, INT.notion],
  },
  {
    surface: "imessage",
    sender: "You",
    dept: "Memory",
    user: "Why did that outreach sequence convert so well?",
    agent:
      "Ran a post-mortem on the 217-lead nurture. Subject line pattern + Tuesday 9am send did the lift. Saved as `warm-vp-nurture` and queued for the next 3 campaigns.",
    deliverable: { title: "Pattern learned", label: "Reply rate", value: "11.4%", positive: true },
    integrations: [INT.hubspot, INT.gmail, INT.notion],
  },

  // — Security —
  {
    surface: "slack",
    channel: "security",
    sender: "Ravi",
    dept: "Security",
    user: "Critical CVE just dropped. Are we exposed?",
    agent:
      "Scanned every repo. 4 services pin the vulnerable version. Patched on bump/cve-4427, tests green, PRs open with rollout plan.",
    deliverable: { title: "Exposure contained", label: "Services patched", value: "4 / 4", positive: true },
    integrations: [INT.snyk, INT.github, INT.sentry, INT.datadog],
  },
  {
    surface: "teams",
    channel: "it-secops",
    sender: "Hannah",
    dept: "SecOps",
    user: "@Mane audit who has prod access. Revoke anything stale.",
    agent:
      "Reviewed 213 grants across AWS, GitHub and 1Password. 27 dormant > 90 days, revoked. SOC 2 evidence saved to the audit folder.",
    deliverable: { title: "Access tightened", label: "Stale grants pulled", value: "27", positive: true },
    integrations: [INT.onepassword, INT.cloudflare, INT.github, INT.notion],
  },

  // — Dev (more) —
  {
    surface: "slack",
    channel: "platform",
    sender: "Jordan",
    dept: "DevOps",
    user: "P99 latency just doubled on checkout. Find it.",
    agent:
      "Datadog spike traced to a new N+1 in the cart service. Patch on fix/cart-nplus1, P99 back to 142ms in staging, PR open.",
    deliverable: { title: "Regression fixed", label: "P99 restored", value: "142ms", positive: true },
    integrations: [INT.datadog, INT.sentry, INT.github, INT.linear],
  },
  {
    surface: "imessage",
    sender: "You",
    dept: "QA",
    user: "Write the test suite I never got around to.",
    agent:
      "Authored 142 unit + 28 E2E tests across the checkout flow. Coverage 31% → 84%. CI passes, PR open.",
    deliverable: { title: "Coverage shipped", label: "31% → 84%", value: "+53pt", positive: true },
    integrations: [INT.github, INT.cypress, INT.vercel],
  },

  // — Growth / GTM —
  {
    surface: "slack",
    channel: "growth",
    sender: "Maya",
    dept: "Growth",
    user: "Activation dropped 9% last week. Diagnose and run a test.",
    agent:
      "PostHog says step-2 drop after the copy change on Nov 4. Reverted, shipped a 3-arm test on the headline, gates on 5% traffic.",
    deliverable: { title: "Experiment live", label: "Arms running", value: "3", positive: true },
    integrations: [INT.posthog, INT.amplitude, INT.segment, INT.github],
  },
  {
    surface: "mane",
    dept: "GTM",
    user: "Plan the Series B launch. Press, content, ads, sales enablement.",
    agent:
      "12-week GTM mapped: 4 launch beats, 18 assets, 2 press exclusives, sales one-pager and battle cards. Owners assigned, calendar booked.",
    deliverable: { title: "Launch plan", label: "Assets queued", value: "18", positive: true },
    integrations: [INT.notion, INT.linear, INT.linkedin, INT.hubspot],
  },

  // — Product / Design —
  {
    surface: "slack",
    channel: "product",
    sender: "Nina",
    dept: "Product",
    user: "Synthesize the 47 user interviews into a roadmap.",
    agent:
      "Clustered 312 quotes into 9 themes. Top 3 map to revenue, drafted PRDs for each, prioritised by RICE. Roadmap pushed to Linear.",
    deliverable: { title: "Roadmap built", label: "Initiatives", value: "9", positive: true },
    integrations: [INT.notion, INT.linear, INT.jira, INT.asana],
  },
  {
    surface: "telegram",
    sender: "You",
    dept: "App",
    user: "Build me an internal app for the team to log expenses.",
    agent:
      "Coded the form, auth, approvals and CSV export. Tailwind UI in your brand, deployed to Vercel, role-gated for finance. Link in Slack.",
    attachment: {
      src: internalAppPreview,
      caption: "expenses.acme.internal · live · role-gated to finance",
      alt: "Internal expense tracking app with sidebar nav and submit form",
    },
    deliverable: { title: "Internal app live", label: "Time to ship", value: "38 min", positive: true },
    integrations: [INT.github, INT.vercel, INT.tailwind, INT.slack],
  },

  // — Data / Analytics —
  {
    surface: "mane",
    dept: "Data",
    user: "Why did weekly retention drop two cohorts in a row?",
    agent:
      "Cross-checked Mixpanel, Amplitude and Segment. Drop isolates to mobile signups from paid social. Funnel breaks at onboarding step 3, brief in Notion.",
    deliverable: { title: "Root cause", label: "Cohorts affected", value: "2", positive: false },
    integrations: [INT.mixpanel, INT.amplitude, INT.segment, INT.posthog],
  },

  // — VA / Executive assistant —
  {
    surface: "whatsapp",
    dept: "Assistant",
    user: "Book me 4 customer dinners in NYC next week. Steakhouses.",
    agent:
      "Held 4 slots Mon–Thu, sent Calendly invites with venues + dietary asks. Reservations confirmed at Keens, Quality, Wolfgang's and Carbone.",
    deliverable: { title: "Dinners booked", label: "Confirmed", value: "4 / 4", positive: true },
    integrations: [INT.calendly, INT.gcal, INT.gmail],
  },
  {
    surface: "imessage",
    sender: "You",
    dept: "Assistant",
    user: "Plan the Lisbon trip for the offsite. 8 people, 4 nights.",
    agent:
      "Flights compared and held, 4-bed villa in Príncipe Real booked, 6 dinners reserved, agenda + travel doc shared. Total $14,820 vs $19k budget.",
    deliverable: { title: "Trip planned", label: "Under budget", value: "-$4,180", positive: true },
    integrations: [INT.gcal, INT.gmail, INT.notion, INT.drive],
  },

  // — Support / CX —
  {
    surface: "slack",
    channel: "support",
    sender: "Sam",
    dept: "Support",
    user: "Backlog is 312 tickets. Burn it down overnight.",
    agent:
      "Auto-resolved 188 with KB answers, escalated 41 to engineering with repro steps, drafted replies for the remaining 83 in your tone.",
    deliverable: { title: "Backlog cleared", label: "Resolved", value: "229 / 312", positive: true },
    integrations: [INT.intercom, INT.zendesk, INT.linear, INT.slack],
  },

  // — HR / People —
  {
    surface: "mane",
    dept: "People",
    user: "Run the quarterly review cycle. Nudge managers.",
    agent:
      "Opened 84 reviews in Rippling, sent calibrated prompts to 17 managers, surfaced 6 risk flags from sentiment. Weekly status to you on Fridays.",
    deliverable: { title: "Cycle launched", label: "Reviews opened", value: "84", positive: true },
    integrations: [INT.rippling, INT.greenhouse, INT.notion, INT.slack],
  },

  // — Legal / contracts —
  {
    surface: "imessage",
    sender: "You",
    dept: "Legal",
    user: "Red-line the MSA Acme sent. Track our standard positions.",
    agent:
      "Compared against your playbook. 14 deviations, 6 material. Counter-language inserted, rationale in margin notes. Ready in DocuSign for review.",
    deliverable: { title: "Red-line ready", label: "Material flags", value: "6", positive: false },
    integrations: [INT.docusign, INT.notion, INT.drive],
  },

  // — E-commerce / lifecycle —
  {
    surface: "telegram",
    sender: "You",
    dept: "E-commerce",
    user: "Cart abandonment is up. Win them back.",
    agent:
      "3-step Klaviyo flow live, dynamic discount tiers by AOV, Shopify product blocks pulled in. Forecast $42k recovered this month.",
    deliverable: { title: "Win-back live", label: "Forecast recovery", value: "$42k", positive: true },
    integrations: [INT.klaviyo, INT.shopify, INT.mailchimp, INT.meta],
  },

  // — Scheduled / recurring tasks —
  {
    surface: "slack",
    channel: "exec",
    sender: "You",
    dept: "Recurring",
    user: "Every Monday 7am, send me the weekly business review.",
    agent:
      "Scheduled. Every Mon 7am local: pull Stripe, HubSpot, PostHog and Linear, draft the 1-page review with deltas vs last week, DM it to you with a thread for the team.",
    attachment: {
      src: wbrDashboardPreview,
      caption: "wbr · week 22 · DM'd Mon 7:00 local",
      alt: "Weekly business review dashboard with KPI cards and revenue chart",
    },
    deliverable: { title: "Weekly job live", label: "Next run", value: "Mon · 7:00", positive: true },
    integrations: [INT.stripe, INT.hubspot, INT.posthog, INT.linear],
  },
  {
    surface: "mane",
    dept: "Monitoring",
    user: "Watch our pricing page every hour. Ping me if a competitor changes theirs.",
    agent:
      "Hourly crawl set on 6 competitor pricing pages. Diff stored, screenshots archived. Pings you only on a real change with a side-by-side.",
    deliverable: { title: "Watcher running", label: "Cadence", value: "Hourly · 24/7", positive: true },
    integrations: [INT.notion, INT.slack, INT.drive],
  },
  {
    surface: "telegram",
    sender: "You",
    dept: "Recurring",
    user: "Every weekday 5pm, post a recap of what shipped today.",
    agent:
      "Standing job created. Pulls merged PRs, closed Linear issues and Sentry resolutions, drafts the recap in your voice, posts to #shipped at 5pm Mon–Fri.",
    deliverable: { title: "Daily recap live", label: "Channel", value: "#shipped", positive: true },
    integrations: [INT.github, INT.linear, INT.sentry, INT.slack],
  },

  // — Customer support —
  {
    surface: "slack",
    channel: "support",
    sender: "You",
    dept: "Support",
    user: "Clear the Intercom queue. Escalate only the real fires.",
    agent:
      "Answered 184 tickets from the help center and past resolutions, refunded 9 per policy, escalated 6 with full context and Sentry traces attached.",
    deliverable: { title: "Queue cleared", label: "Tickets resolved", value: "184 / 199", positive: true },
    integrations: [INT.intercom, INT.zendesk, INT.sentry, INT.stripe],
  },
  {
    surface: "telegram",
    dept: "Support",
    user: "A customer is furious about a double charge. Fix it.",
    agent:
      "Pulled the Stripe ledger, confirmed the duplicate, refunded $248, sent an apology with a credit, opened a Linear ticket on the root cause in checkout.",
    deliverable: { title: "Refunded + recovered", label: "NPS recovered", value: "−2 → +9", positive: true },
    integrations: [INT.stripe, INT.intercom, INT.linear],
  },

  // — Travel / booking —
  {
    surface: "imessage",
    sender: "You",
    dept: "Travel",
    user: "Book me JFK → LHR next Thu, back Sun. Aisle, under $1.4k, lounge access.",
    agent:
      "Held BA 178 out Thu 9:40pm, BA 117 back Sun 11:15am. Aisle 14C both legs, $1,284 total, Amex lounge cleared. Reply 'book' to charge the Brex card.",
    deliverable: { title: "Flights held", label: "Under budget", value: "−$116", positive: true },
    integrations: [INT.gcal, INT.brex, INT.gmail],
  },
  {
    surface: "whatsapp",
    dept: "Travel",
    user: "Plan the Lisbon offsite. 8 people, 4 nights, walking distance to the venue.",
    agent:
      "Held 8 rooms at Memmo Alfama, 3 dinner reservations, 2 group transfers, calendar invites sent. Total $11,840 under the $13k cap.",
    deliverable: { title: "Offsite booked", label: "Confirmations", value: "8 / 8", positive: true },
    integrations: [INT.gcal, INT.gmail, INT.notion, INT.brex],
  },

  // — Research / due diligence —
  {
    surface: "mane",
    dept: "Research",
    user: "Deep dive on the agent-infra category. Players, traction, moats, who to watch.",
    agent:
      "Pulled 312 sources across Crunchbase, GitHub, PostHog public dashboards, hiring posts and earnings calls. 14-page brief with comp grid, share-of-voice, and 6 names to watch.",
    deliverable: { title: "Market brief ready", label: "Sources cited", value: "312", positive: true },
    integrations: [INT.perplexity, INT.notion, INT.github, INT.linkedin],
  },
  {
    surface: "slack",
    channel: "deals",
    sender: "You",
    dept: "Research",
    user: "Run diligence on Acme Robotics before our Friday call.",
    agent:
      "Financials, cap table, customer references, GitHub velocity, churn signals from Glassdoor and LinkedIn moves. Red-flag memo with 4 questions to ask, sent to the deal room.",
    deliverable: { title: "Diligence pack", label: "Red flags", value: "3", positive: false },
    integrations: [INT.notion, INT.linkedin, INT.github, INT.drive],
  },

  // — Proactive: Mane initiates without a prompt —
  {
    surface: "slack",
    channel: "growth",
    dept: "Growth",
    user: "",
    agent:
      "Conversion on /pricing dropped 18% in the last 4 hours. Traced it to a broken Stripe webhook after the deploy at 14:02. Rolled back the offending commit, queued a postmortem in Linear, paid signups are recovering.",
    deliverable: { title: "Outage caught + fixed", label: "Revenue saved", value: "$11,200", positive: true },
    integrations: [INT.stripe, INT.posthog, INT.sentry, INT.github, INT.linear],
    proactive: true,
  },
  {
    surface: "telegram",
    dept: "Security",
    user: "",
    agent:
      "Spotted an AWS key in commit 9f3a2c1 pushed 2 minutes ago. Rotated the key, force-pushed a clean history, opened a Snyk finding, DM'd the author with the new secret in 1Password.",
    deliverable: { title: "Leak contained", label: "Exposure window", value: "118s", positive: true },
    integrations: [INT.github, INT.snyk, INT.onepassword, INT.cloudflare],
    proactive: true,
  },
];

// Mobile gets a chunkier typing cadence: 2 chars per tick at a slightly
// longer interval. Same perceived speed, ~half the React renders.
const IS_MOBILE =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(max-width: 767px)")?.matches ?? false);
const TYPE_MS = IS_MOBILE ? 24 : 16;
const TYPE_STEP = IS_MOBILE ? 2 : 1;
const PAUSE_BEFORE_AGENT = 700;
// Hold times bumped — 5600/3400ms wasn't long enough on either device
// for the eye to read the agent body + study the deliverable card.
// New mobile: 9000ms (3.4s longer), desktop: 5200ms (1.8s longer).
const HOLD = IS_MOBILE ? 9000 : 5200;
const FADE = IS_MOBILE ? 620 : 420;
// Mobile image-card scripts (pricing table, dashboard, landing page, etc.)
// hold longer than text-only scripts because the image takes more time
// to "read". Bumped 17s → 22s based on feedback that images were
// cycling out before the user finished seeing them.
const mobileMediaHold = (script: Script) => (IS_MOBILE && script.attachment ? 22000 : 0);
const mobileMediaIntroPause = (script: Script) => (IS_MOBILE && script.attachment ? 400 : PAUSE_BEFORE_AGENT);
const thinkingPause = (script: Script) => (IS_MOBILE && script.attachment ? 700 : 900);
const keepsMediaPinned = (script: Script) => IS_MOBILE && Boolean(script.attachment);

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Brand logo via simple-icons CDN. White override for brands whose mark is
// black so they stay visible on the dark glass card.
const logoSrc = (i: Integration) =>
  i.light
    ? `https://cdn.simpleicons.org/${i.slug}/ffffff`
    : `https://cdn.simpleicons.org/${i.slug}`;

// Slugs that Simple Icons has removed from the CDN (mostly trademark-policy
// pulls — Slack, LinkedIn, Salesforce, etc.). Requesting them logs a 404 to
// the browser console which Lighthouse counts against Best Practices. We
// pre-empt the request entirely and render a letter-initial fallback in JSX
// instead of <img>, so no 404 ever fires.
//
// Verified against the live CDN — keep in sync if simple-icons restores or
// removes more brand marks. Audit script:
//   for slug in <list>; do curl -sI cdn.simpleicons.org/$slug -w "%{http_code}\n" -o /dev/null; done
const KNOWN_BAD_SIMPLEICONS = new Set<string>([
  "ahrefs",
  "amplitude",
  "apollo",
  "attio",
  "canva",
  "clay",
  "docusign",
  "klaviyo",
  "linkedin",
  "microsoftoutlook",
  "pipedrive",
  "rippling",
  "salesforce",
  "segment",
  "slack",
]);

// Reorder so scripts with image/file attachments lead the rotation and are
// interleaved with talk-only scripts. Without this, the 10 media-rich scripts
// sit deep in the 59-item array and most viewers never see them.
const ORDERED_SCRIPTS: Script[] = (() => {
  const withMedia = SCRIPTS.filter((s) => s.attachment);
  const withoutMedia = SCRIPTS.filter((s) => !s.attachment);
  const out: Script[] = [];
  const max = Math.max(withMedia.length, withoutMedia.length);
  for (let i = 0; i < max; i++) {
    if (i < withMedia.length) out.push(withMedia[i]);
    if (i < withoutMedia.length) out.push(withoutMedia[i]);
  }
  return out;
})();

const HeroAgentChat = () => {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"user" | "thinking" | "agent" | "deliverable" | "out">("user");
  const [userText, setUserText] = useState("");
  const [agentText, setAgentText] = useState("");
  // No JS scroll handling — `.mane-thread` uses flex-end +
  // overflow:hidden, so as items mount in DOM order they're pushed
  // up by new content and the OLDEST clips off the top edge (faded
  // by the top mask). Pure CSS handles the "newest at bottom" feel.

  useEffect(() => {
    let cancelled = false;
    const s = ORDERED_SCRIPTS[idx];

    const run = async () => {
      setUserText("");
      setAgentText("");
      setPhase("user");

      if (!s.proactive) {
        for (let c = TYPE_STEP; c <= s.user.length; c += TYPE_STEP) {
          if (cancelled) return;
          setUserText(s.user.slice(0, Math.min(c, s.user.length)));
          await wait(TYPE_MS);
        }
        if (s.user.length % TYPE_STEP !== 0) setUserText(s.user);
        await wait(mobileMediaIntroPause(s));
        if (cancelled) return;
      } else {
        // Proactive: brief beat before Mane speaks up on its own.
        await wait(420);
        if (cancelled) return;
      }

      setPhase("thinking");
      await wait(thinkingPause(s));
      if (cancelled) return;

      setPhase("agent");
      for (let c = TYPE_STEP; c <= s.agent.length; c += TYPE_STEP) {
        if (cancelled) return;
        setAgentText(s.agent.slice(0, Math.min(c, s.agent.length)));
        await wait(TYPE_MS);
      }
      if (s.agent.length % TYPE_STEP !== 0) setAgentText(s.agent);
      await wait(420 + mobileMediaHold(s));
      if (cancelled) return;

      if (keepsMediaPinned(s)) {
        setPhase("out");
        await wait(FADE);
        if (cancelled) return;

        setIdx((i) => (i + 1) % ORDERED_SCRIPTS.length);
        return;
      }

      setPhase("deliverable");
      await wait(HOLD);
      if (cancelled) return;

      setPhase("out");
      await wait(FADE);
      if (cancelled) return;

      setIdx((i) => (i + 1) % ORDERED_SCRIPTS.length);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [idx]);

  const s = ORDERED_SCRIPTS[idx];
  const showUser = phase !== "out";
  const showAgent = phase === "agent" || phase === "deliverable";
  const showThinking = phase === "thinking";
  const showDeliverable = phase === "deliverable" && !keepsMediaPinned(s);

  const surface: Surface = s.surface ?? "mane";
  const surfaceLabel: Record<Surface, string> = {
    mane: "Mane chat",
    slack: "Slack",
    imessage: "iMessage",
    telegram: "Telegram",
    whatsapp: "WhatsApp",
    teams: "Microsoft Teams",
  };

  return (
    <div
      className="mane-stack"
      data-out={phase === "out"}
      data-surface={surface}
      data-media={s.attachment ? "true" : "false"}
      data-phase={phase}
    >
      <div className="mane-channel-lock" data-show={showUser}>
        <div className="surface-eyebrow" data-show={showUser}>
          <span className="surface-eyebrow-dot" data-surface={surface} aria-hidden />
          <span className="surface-eyebrow-label">via {surfaceLabel[surface]}</span>
        </div>
        {surface === "slack" && (
          <div className="surface-slack-header" data-show={showUser}>
          <span className="surface-slack-logo" aria-hidden>
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path fill="#E01E5A" d="M5 15a2 2 0 1 1-2-2h2v2Zm1 0a2 2 0 0 1 4 0v5a2 2 0 0 1-4 0v-5Z"/>
              <path fill="#36C5F0" d="M9 5a2 2 0 1 1 2-2v2H9Zm0 1a2 2 0 0 1 0 4H4a2 2 0 0 1 0-4h5Z"/>
              <path fill="#2EB67D" d="M19 9a2 2 0 1 1 2 2h-2V9Zm-1 0a2 2 0 0 1-4 0V4a2 2 0 0 1 4 0v5Z"/>
              <path fill="#ECB22E" d="M15 19a2 2 0 1 1-2 2v-2h2Zm0-1a2 2 0 0 1 0-4h5a2 2 0 0 1 0 4h-5Z"/>
            </svg>
          </span>
          <span className="surface-slack-hash">#</span>
          <span className="surface-slack-channel">{s.channel ?? "general"}</span>
          <span className="surface-slack-sep">/</span>
          <span className="surface-slack-meta">mane-agent</span>
          </div>
        )}

        {surface === "imessage" && (
          <div className="surface-im-header" data-show={showUser}>
          <span className="surface-im-avatar" aria-hidden>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="#fff">
              <path d="M12 3C6.5 3 2 6.7 2 11.2c0 2.4 1.3 4.6 3.4 6.1-.1 1.1-.6 2.5-1.5 3.4-.2.2 0 .5.2.5 1.9-.1 3.6-.7 4.8-1.5 1 .3 2 .4 3.1.4 5.5 0 10-3.7 10-8.2S17.5 3 12 3Z"/>
            </svg>
          </span>
          <div className="surface-im-meta">
            <span className="surface-im-name">Mane</span>
            <span className="surface-im-status">iMessage · active now</span>
          </div>
          </div>
        )}

        {surface === "telegram" && (
          <div className="surface-tg-header" data-show={showUser}>
          <span className="surface-tg-avatar" aria-hidden>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="#fff">
              <path d="M21.6 4.1 2.9 11.3c-1 .4-1 1.7 0 2l4.6 1.4 1.8 5.6c.2.7 1 .9 1.5.4l2.6-2.4 4.7 3.5c.7.5 1.7.1 1.9-.7l3-15.2c.2-1-.8-1.9-1.8-1.5Zm-3 4.1-7.9 7.2c-.2.1-.2.3-.3.5l-.3 2.7-1.4-4.5 9.7-6c.3-.2.5.3.2.5l.3-.4Z"/>
            </svg>
          </span>
          <span className="surface-tg-name">Mane bot</span>
          <span className="surface-tg-status">online</span>
          </div>
        )}

        {surface === "whatsapp" && (
          <div className="surface-wa-header" data-show={showUser}>
          <span className="surface-wa-avatar" aria-hidden>
            <svg viewBox="0 0 32 32" width="18" height="18" fill="#fff" style={{ display: "block" }}>
              <path d="M16 3C8.8 3 3 8.8 3 16c0 2.3.6 4.5 1.7 6.4L3 29l6.8-1.8A12.9 12.9 0 0 0 16 29c7.2 0 13-5.8 13-13S23.2 3 16 3Zm7.5 18.4c-.3.9-1.8 1.7-2.5 1.8-.6.1-1.4.1-2.3-.1-.5-.2-1.2-.4-2.1-.8-3.7-1.6-6.1-5.3-6.3-5.6-.2-.2-1.5-2-1.5-3.9 0-1.8 1-2.8 1.3-3.1.3-.3.7-.4 1-.4h.7c.2 0 .5 0 .8.6.3.7 1 2.4 1.1 2.6.1.2.1.4 0 .6-.1.2-.2.3-.4.5-.2.2-.4.5-.5.6-.2.2-.4.4-.2.7.2.4.9 1.5 2 2.5 1.4 1.2 2.5 1.6 2.9 1.8.4.2.6.1.8-.1.2-.2.9-1.1 1.2-1.4.2-.4.5-.3.8-.2.3.1 2 .9 2.3 1.1.3.2.6.2.7.4.1.2.1 1-.2 1.9Z"/>
            </svg>
          </span>
          <div className="surface-wa-meta">
            <span className="surface-wa-name">Mane</span>
            <span className="surface-wa-status">last seen just now</span>
          </div>
          </div>
        )}

        {surface === "teams" && (
          <div className="surface-teams-header" data-show={showUser}>
            <span className="surface-teams-logo" aria-hidden>
              <svg viewBox="0 0 32 32" width="14" height="14">
                <path fill="#5059C9" d="M20 12h8a2 2 0 0 1 2 2v6a5 5 0 0 1-5 5h0a5 5 0 0 1-5-5V12Z"/>
                <circle cx="25" cy="7" r="3" fill="#5059C9"/>
                <path fill="#7B83EB" d="M2 9h16v14a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V9Z"/>
                <circle cx="14" cy="5" r="4" fill="#7B83EB"/>
                <path fill="#fff" d="M6 13h8v2h-3v8H9v-8H6z"/>
              </svg>
            </span>
            <span className="surface-teams-team">Acme</span>
            <span className="surface-teams-sep">›</span>
            <span className="surface-teams-channel">{s.channel ?? "general"}</span>
            <span className="surface-teams-sep">/</span>
            <span className="surface-teams-meta">Mane (bot)</span>
          </div>
        )}
      </div>

      <div className="mane-thread">
        {!s.proactive && (
          <div className="mane-user" data-show={showUser}>
            {(surface === "slack" || surface === "teams") && s.sender && (
              <span className="surface-slack-sender">{s.sender}</span>
            )}
            <span className="mane-user-text">{userText || "\u00A0"}</span>
          </div>
        )}
        {s.proactive && (
          <div className="mane-proactive" data-show={showUser}>
            <span className="mane-proactive-dot" aria-hidden />
            <span>Mane noticed something</span>
          </div>
        )}

        <div className="mane-agent" data-show={showAgent || showThinking}>
          <div className="mane-agent-head">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" />
            <circle cx="12" cy="12" r="3.2" fill="currentColor" />
          </svg>
          <span>Mane Agent · {s.dept}</span>
          </div>
          {showThinking ? (
            <div
              className="mane-typing"
              role="status"
              aria-live="polite"
              aria-label="Mane is thinking"
            >
              <span aria-hidden /><span aria-hidden /><span aria-hidden />
            </div>
          ) : (
            <>
              {s.attachment && (
                <figure className="mane-attach">
                <img
                  src={s.attachment.src}
                  alt={s.attachment.alt}
                  width={800}
                  height={512}
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="mane-attach-caption">
                  {s.attachment.caption}
                </figcaption>
                </figure>
              )}
              <div className="mane-agent-body">{agentText}</div>
            </>
          )}
        </div>

        <div className="mane-card" data-show={showDeliverable}>
        <div className="mane-card-title">{s.deliverable.title}</div>
        <div className="mane-card-row">
          <div className="mane-card-icon" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="6" width="18" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M3 10h18" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </div>
          <div className="mane-card-text">
            <div className="mane-card-label">{s.deliverable.title}</div>
            <div className="mane-card-sub">{s.deliverable.label}</div>
          </div>
          <div
            className="mane-card-value"
            data-positive={s.deliverable.positive ? "true" : "false"}
          >
            {s.deliverable.value}
          </div>
        </div>
        <div className="mane-card-ints">
          {s.integrations.map((i) => (
            <span key={i.name} className="mane-int">
              {KNOWN_BAD_SIMPLEICONS.has(i.slug) ? (
                // Slug isn't on the CDN — render a letter-initial fallback
                // so the layout slot stays and no 404 is requested.
                <span
                  aria-hidden
                  className="mane-int-logo mane-int-fallback"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 12,
                    height: 12,
                    fontSize: 8,
                    fontWeight: 600,
                    lineHeight: 1,
                    opacity: 0.7,
                  }}
                >
                  {i.name.charAt(0).toUpperCase()}
                </span>
              ) : (
                <img
                  src={logoSrc(i)}
                  alt=""
                  aria-hidden
                  width={12}
                  height={12}
                  loading="lazy"
                  decoding="async"
                  className="mane-int-logo"
                  onError={(e) => {
                    // Defensive — slug we haven't blacklisted yet still 404s.
                    // Swap the broken <img> for a monogram chip so the row
                    // always looks intentional.
                    const img = e.currentTarget;
                    const fb = document.createElement("span");
                    fb.className = "mane-int-fallback";
                    fb.textContent = i.name.charAt(0);
                    img.replaceWith(fb);
                  }}
                />
              )}
              {i.name}
            </span>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default HeroAgentChat;
